import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useGetFrequency,
  useGetMeterType,
} from "@features/mx/readCycleManagement/hooks";
import { useNavigate } from "react-router-dom";

interface ScheduleFilters {
  frequency: string;
  status: string;
  readType: string;
  startDate: string;
  endDate: string;
}

interface ScheduleFilterPanelProps {
  frequency: string;
  status: string;
  readType: string;
  startDate: string;
  endDate: string;
  onApplyFilters: (filters: ScheduleFilters) => void;
  onResetFilters: () => void;
}

export const ScheduleFilterPanel: React.FC<ScheduleFilterPanelProps> = ({
  frequency,
  status,
  readType,
  startDate,
  endDate,
  onApplyFilters,
  onResetFilters,
}) => {
  const navigate = useNavigate();
  const { data: frequencyData } = useGetFrequency();
  const { data: meterTypeData } = useGetMeterType();
  // Define filter options
  const frequencyOptions = useMemo(() => {
    return frequencyData?.map((frequency) => ({
      value: frequency.key,
      label: frequency.value,
    }));
  }, [frequencyData]);

  const statusOptions = useMemo(
    () => [
      { value: "Paused", label: "Paused" },
      { value: "Active", label: "Active" },
      { value: "Scheduled", label: "Scheduled" },
      { value: "Overdue", label: "Overdue" },
      { value: "Completed", label: "Completed" },
    ],
    []
  );

  const readTypeOptions = useMemo(
    () =>
      meterTypeData?.map((meterType) => ({
        value: meterType.value,
        label: meterType.value,
      })),
    [meterTypeData]
  );

  // Define filter fields
  const filterFields: FormField[] = [
    {
      name: "frequency",
      label: "Frequency",
      type: "select",
      required: false,
      placeholder: "All Frequencies",
      options: frequencyOptions,
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "status",
      label: "Status",
      type: "select",
      required: false,
      placeholder: "All Statuses",
      options: statusOptions,
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "readType",
      label: "Read Type",
      type: "select",
      required: false,
      placeholder: "All Types",
      options: readTypeOptions,
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "startDate",
      label: "Start Date",
      type: "datepicker",
      required: false,
      placeholder: "Start Date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
    {
      name: "endDate",
      label: "End Date",
      type: "datepicker",
      required: false,
      placeholder: "End Date",
      className: "col-span-1",
      classes: {
        container: "w-full mb-6",
      },
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onSubmit",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      frequency: frequency || "",
      status: status || "",
      readType: readType || "",
      startDate: startDate || "",
      endDate: endDate || "",
    });
  }, [frequency, status, readType, startDate, endDate, form]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      frequency: formValues.frequency || "",
      status: formValues.status || "",
      readType: formValues.readType || "",
      startDate: formValues.startDate || "",
      endDate: formValues.endDate || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      frequency: "",
      status: "",
      readType: "",
      startDate: "",
      endDate: "",
    });
    onResetFilters();
    navigate(`/mx/read-cycle/?tab=schedules`);
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add field-specific logic if needed
    // For example, validate end date is after start date
    if (field === "startDate") {
      const endDateValue = form.getValues("endDate");
      if (endDateValue && value && new Date(value) > new Date(endDateValue)) {
        form.setValue("endDate", "");
      }
    }
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={4}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-2 mt-4",
          },
        }}
      />
    </div>
  );
};
